// filepath: apps/web/src/renderers/__shared__/serverShape.ts
//
// FR-UI-CORE-4 shared utility: the single source of truth for
// "renderer iterates server-returned fields only" — the SPA-side mirror
// of FR-AUTH-7's server-side field stripping (INV-2 boundary).
//
// Why this module exists:
//   The form renderer (apps/web/src/renderers/form/FormRenderer.tsx)
//   already follows the contract via hiddenFields + HiddenField. The
//   next renderers (entity detail, process instance viewer, list
//   detail panel) would each re-derive the same invariants if there
//   were no shared utility. This module is the single point of truth
//   for AC-1, AC-2, and AC-3 — every renderer imports these helpers
//   rather than reimplementing the guard locally.
//
// Contract:
//   - getDefinitionFieldIds(definition) returns the server's id set in
//     insertion order (the order the tenant authored them, which
//     ui_schema.order overrides downstream).
//   - isInServerShape(fieldId, definition) is the explicit guard every
//     renderer uses before creating a field-slot. AC-2 invariant:
//     fields NOT in the server response MUST NOT be rendered, even if
//     initialValues contains them or a parent passes a defaultFields
//     schema prop.
//   - resolveHiddenFieldAction(hint) returns the discriminated action
//     the renderer must take: 'render' | 'hidden-input' | 'omit'.
//     - 'permission' → 'omit' (server-stripped; INV-2 / FR-AUTH-7 — no
//       slot, no label, no shell)
//     - 'formula'    → 'hidden-input' (cosmetic hide; the value is
//       preserved server-side and may re-render when the formula's
//       inputs change)
//     - undefined / unknown → 'render' (safe default)
//
// INVARIANTS:
//   - No client-side schema fallback (no FieldRegistry.byEntityType).
//   - No iteration off initialValues / recordData.
//   - No client-side formula evaluation (FR-FRM-1/2/4 server-side-only).
//   - The order of field ids returned by getDefinitionFieldIds matches
//     the server's payload insertion order (NOT Object.keys() iteration
//     order, which is the same in modern JS engines but documented as
//     server-authoritative here).

import type { FieldMetadata } from "@/definitions/types";

/** Discriminated action for a hidden-fields hint from the server. */
export type HiddenFieldAction = "render" | "hidden-input" | "omit";

/**
 * The hidden-fields map shape returned by the server-side formula
 * evaluator (apps/web/src/renderers/form/useFieldEvaluations.ts and
 * any future equivalent for entity / process renderers).
 *
 * - "permission" — server stripped this field (FR-AUTH-7). Renderer
 *   MUST omit it entirely. This is a *security* signal.
 * - "formula" — a visibility formula returned false. Renderer MUST
 *   render a non-input placeholder and preserve the value in the
 *   onChange payload so it survives a formula flip. This is a
 *   *cosmetic* signal (INV-2 boundary).
 */
export type HiddenFieldHint = "permission" | "formula";

export type HiddenFieldsMap = Record<string, HiddenFieldHint | undefined>;

/**
 * Return the field ids in the order the server returned them.
 *
 * AC-1 invariant: the renderer iterates these ids, and ONLY these
 * ids, when deciding which field-slots to render. Order is preserved
 * because the tenant's `ui_schema.order` typically references these
 * keys directly; downstream resolvers (`resolveFieldOrder` in the
 * form renderer) may re-order by `ui_schema.order` after this.
 */
export function getDefinitionFieldIds(
  definition: Record<string, FieldMetadata | string | undefined>,
): string[] {
  return Object.keys(definition).filter((k) => k !== "entityType");
}

/**
 * AC-2 invariant guard: a field id is in the server's response iff
 * `isInServerShape(id, definition)` is true. Every renderer MUST call
 * this before creating a field-slot — including for keys found in
 * `initialValues`, in a parent-passed `defaultFields` schema, in a
 * URL hint, or in any TypeScript-typed fallback.
 */
export function isInServerShape(
  fieldId: string,
  definition: Record<string, FieldMetadata | string | undefined>,
): boolean {
  if (fieldId === "entityType") return false;
  return Object.prototype.hasOwnProperty.call(definition, fieldId);
}

/**
 * AC-3 discriminator: given the server's `hiddenFields[id]` hint,
 * return the action the renderer must take.
 *
 * - `'permission'` → `'omit'`  (server stripped; FR-AUTH-7 / INV-2)
 * - `'formula'`    → `'hidden-input'`  (cosmetic; preserve value)
 * - `undefined` / unknown → `'render'` (safe default)
 *
 * The discriminated union makes the type system reject unknown
 * branches; the safe-default behavior makes a malformed server
 * payload degrade to "render the field" rather than silently omitting
 * (which would lose data).
 */
export function resolveHiddenFieldAction(
  hint: HiddenFieldHint | undefined,
): HiddenFieldAction {
  if (hint === "permission") return "omit";
  if (hint === "formula") return "hidden-input";
  return "render";
}
